import React, { useState } from 'react';
import {
  Button,
  Flex,
  Form,
  Input,
  InputNumber,
  notification,
  Tag,
  Typography,
} from '@duxton-web/ui';

import { getTenantSeqNumber } from '@/common/Tenants';
import { useCreateAdminService } from '@/hooks/usePlannerQueries';

interface ServiceOnboardingFormValues {
  prefix: string;
  title: string;
  techFamily: string;
}

const PREFIX_PATTERN = /^[a-z][a-z0-9_.-]*$/;

const DEFAULT_TENANT_SEQ = getTenantSeqNumber();

const ServiceOnboardingTab: React.FC = () => {
  const [form] = Form.useForm<ServiceOnboardingFormValues>();
  const { mutateAsync: createAdminService, isPending } =
    useCreateAdminService();

  const [tenantSeqs, setTenantSeqs] = useState<number[]>([DEFAULT_TENANT_SEQ]);
  const [inputSeq, setInputSeq] = useState<number | null>(null);

  const handleAddSeq = () => {
    if (inputSeq === null) return;
    if (!tenantSeqs.includes(inputSeq)) {
      setTenantSeqs((prev) => [...prev, inputSeq]);
    }
    setInputSeq(null);
  };

  const handleRemoveSeq = (seq: number) => {
    setTenantSeqs((prev) => prev.filter((s) => s !== seq));
  };

  const handleSubmit = async () => {
    let values: ServiceOnboardingFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const seqs = tenantSeqs.length > 0 ? tenantSeqs : [DEFAULT_TENANT_SEQ];

    try {
      await createAdminService({
        service: {
          prefix: values.prefix,
          title: values.title,
          techFamily: values.techFamily,
          description: '',
        },
        tenantSeqs: seqs,
      });
      notification.success({ message: 'Service onboarded successfully' });
      form.resetFields();
      setTenantSeqs([DEFAULT_TENANT_SEQ]);
      setInputSeq(null);
    } catch (err) {
      notification.error({
        message: 'Failed to onboard service',
        description:
          err instanceof Error ? err.message : 'Unexpected error happened.',
      });
    }
  };

  return (
    <Flex vertical gap={16}>
      <Typography.Text type="secondary">
        Onboard a new service by providing its details below.
      </Typography.Text>

      <Form form={form} layout="vertical" style={{ maxWidth: 480 }}>
        <Form.Item
          label="Prefix"
          name="prefix"
          rules={[
            { required: true, message: 'Prefix is required.' },
            {
              pattern: PREFIX_PATTERN,
              message:
                'Prefix must start with a lowercase letter and contain only lowercase letters, digits, underscores, hyphens, or dots.',
            },
          ]}
        >
          <Input placeholder="e.g. my-service" />
        </Form.Item>

        <Form.Item
          label="Title"
          name="title"
          rules={[
            { required: true, whitespace: true, message: 'Title is required.' },
          ]}
        >
          <Input placeholder="e.g. My Service" />
        </Form.Item>

        <Form.Item
          label="Tech Family"
          name="techFamily"
          rules={[
            {
              required: true,
              whitespace: true,
              message: 'Tech Family is required.',
            },
          ]}
        >
          <Input placeholder="e.g. grab-x" />
        </Form.Item>

        <Form.Item label="Tenant Seqs">
          <Flex vertical gap={8}>
            <Flex gap={8} wrap="wrap">
              {tenantSeqs.map((seq) => (
                <Tag key={seq} closable onClose={() => handleRemoveSeq(seq)}>
                  {seq}
                </Tag>
              ))}
            </Flex>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Tenant Seqs:{' '}
              {tenantSeqs.length > 0
                ? tenantSeqs.join(', ')
                : `${DEFAULT_TENANT_SEQ} (default)`}
            </Typography.Text>
            <Flex gap={8}>
              <InputNumber
                min={1}
                value={inputSeq}
                onChange={(v) => setInputSeq(v)}
                onPressEnter={handleAddSeq}
                placeholder="Add seq number"
                style={{ width: 160 }}
              />
              <Button
                size="small"
                onClick={handleAddSeq}
                disabled={inputSeq === null}
              >
                Add
              </Button>
            </Flex>
          </Flex>
        </Form.Item>

        <Flex justify="flex-end">
          <Button
            type="primary"
            size="small"
            loading={isPending}
            onClick={handleSubmit}
          >
            Onboard Service
          </Button>
        </Flex>
      </Form>
    </Flex>
  );
};

export default ServiceOnboardingTab;
